import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const footerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = footerRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <footer ref={footerRef} className="relative w-full bg-background-100 rounded-t-[32px] px-6 md:px-12 lg:px-20 pt-16 md:pt-20 pb-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 mb-12">
          <div className="reveal opacity-0">
            <img
              src="https://static.readdy.ai/image/1b4fc1192077a5c5be550d1985b79115/2c509e3c57a3303ddf9b8f0cb154ae64.jpeg"
              alt="VOXEA"
              className="h-[75px] w-auto object-contain mb-4"
            />
            <p className="font-body text-sm text-foreground-600 leading-relaxed max-w-xs">
              Clinical diagnostics through voice biomarker technology. Transforming healthcare with AI-powered voice analysis.
            </p>
          </div>

          <div className="reveal opacity-0" style={{ animationDelay: '0.1s' }}>
            <h4 className="font-body text-xs font-semibold tracking-widest text-foreground-50 uppercase mb-6">
              Explore
            </h4>
            <nav className="flex flex-col gap-3">
              <Link to="/" className="font-body text-sm text-foreground-600 hover:text-foreground-50 transition-colors duration-500 ease-silk w-fit">
                Home
              </Link>
              <Link to="/pipeline" className="font-body text-sm text-foreground-600 hover:text-foreground-50 transition-colors duration-500 ease-silk w-fit">
                Pipeline
              </Link>
              <Link to="/contact" className="font-body text-sm text-foreground-600 hover:text-foreground-50 transition-colors duration-500 ease-silk w-fit">
                Contact
              </Link>
              <a href="#technology" className="font-body text-sm text-foreground-600 hover:text-foreground-50 transition-colors duration-500 ease-silk w-fit">
                Technology
              </a>
            </nav>
          </div>

          <div className="reveal opacity-0" style={{ animationDelay: '0.2s' }}>
            <h4 className="font-body text-xs font-semibold tracking-widest text-foreground-50 uppercase mb-6">
              Contact
            </h4>
            <p className="font-body text-sm text-foreground-600 mb-6">
              contactus@voxone.ai
            </p>
            <div className="flex items-center gap-4">
              {[
                { icon: 'ri-linkedin-fill', label: 'LinkedIn' },
                { icon: 'ri-twitter-x-line', label: 'Twitter' },
              ].map((social) => (
                <a
                  key={social.label}
                  href="#"
                  aria-label={social.label}
                  className="w-10 h-10 rounded-full border border-foreground-600/30 flex items-center justify-center text-foreground-600 hover:text-foreground-50 hover:border-foreground-50/50 transition-all duration-500 ease-silk hover:scale-110"
                >
                  <i className={`${social.icon} w-5 h-5 flex items-center justify-center`} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="h-px bg-primary-500/20 mb-8" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-body text-xs text-foreground-700">
            &copy; 2026 VOXEA. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="font-body text-xs text-foreground-700 hover:text-foreground-600 transition-colors duration-500 ease-silk">
              Privacy Policy
            </a>
            <a href="#" className="font-body text-xs text-foreground-700 hover:text-foreground-600 transition-colors duration-500 ease-silk">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}